import { useQuery } from '@tanstack/react-query';
import { apiGet, operationsClient } from '@/lib/api/client';
import { serviceAreaEndpoints } from '@/lib/api/endpoints';
import { serviceAreaKeys } from '@/lib/queryKeys';

/**
 * A single ZIP a place search resolved to, enriched with its named area.
 * `city`/`state` come from the ZIP-centroid dataset; `neighborhood` is only
 * reverse-geocoded for small results and may be null outside mapped areas.
 */
export interface PlaceMatch {
  zipCode: string;
  neighborhood: string | null;
  city: string | null;
  state: string | null;
}

/**
 * The resolved place: the geocoder's readable `label` plus every ZIP the query
 * would add to coverage.
 */
export interface PlaceSearchResult {
  query: string;
  label: string;
  matches: PlaceMatch[];
  /** Areas the place covers, including any the server's cap left out. */
  totalMatches: number;
  /** True when `matches` is the nearest-first subset of `totalMatches`. */
  truncated: boolean;
}

/**
 * Resolve a free-text place query (neighborhood, landmark, city, ZIP, or
 * address) to the ZIP(s) it would add to coverage.
 *
 * Debouncing is left to the caller — pass an already-debounced query so the
 * cache key doesn't churn on every keystroke. The query is disabled below two
 * characters (nothing meaningful geocodes), and results are cached for a few
 * minutes since a place → ZIP mapping is tenant-independent and stable.
 */
export function usePlaceSearch(query: string) {
  const trimmed = query.trim();
  return useQuery<PlaceSearchResult>({
    queryKey: serviceAreaKeys.placeSearch(trimmed),
    queryFn: () =>
      apiGet<PlaceSearchResult>(operationsClient, serviceAreaEndpoints.placeSearch(trimmed)),
    enabled: trimmed.length >= 2,
    staleTime: 5 * 60 * 1000,
  });
}
